// src/Components/Project/ProtectedProjectModal.js
import React, { useState, useEffect, useMemo } from "react";
import {
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Typography,
    Card,
    CardMedia,
    Stack,
    CircularProgress,
    Backdrop,
    alpha,
    useMediaQuery,
    Fab
} from "@mui/material";
import {
    Close as CloseIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Image as ImageIcon,
    Save as SaveIcon,
    Cancel as CancelIcon
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import ProjectForm from "../Forms/ProjectForm";
import ConfirmDeleteModal from "../Forms/ConfirmDeleteModel";
import { api } from "../../config";
import {
    toastSuccess,
    toastError,
    toastInfo,
    toastLoading,
    updateToast
} from "../Common/toastHelper";

const themeColors = {
    primary: "#99191b",
    secondary: "#e3764c",
    success: "#2e7d32",
    error: "#d32f2f"
};

const BG_DETAILS = "#eef2f4";
const MAX_WORDS = 250;

// Mappa nomi categorie → ID (deve combaciare con il backend)
const CATEGORY_MAP = {
    "Sanità": 2,
    "Scolastico": 1,
    "PPP": 3
};

const ProtectedProjectModal = ({ project, isOpen, onClose, onUpdate }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const [currentView, setCurrentView] = useState("details");
    const [formData, setFormData] = useState({
        titolo: "",
        sottotitolo: "",
        citta: "",
        descrizione: "",
        committente: "",
        incarico: "",
        inizio: "",
        fine: "",
        categorie: [],
        is_important: 0,
        isImportant: false,
        images: []
    });
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [loading, setLoading] = useState(false);
    const [showFullDesc, setShowFullDesc] = useState(false);

    // ===== Helpers =====
    const getGalleryImages = () => {
        if (!project) return [];
        if (Array.isArray(project.images) && project.images.length) {
            return [...project.images]
                .sort((a, b) => (a.isHero ? -1 : b.isHero ? 1 : 0))
                .map((img) => ({ path: img.path || img, isHero: !!img.isHero }));
        }
        if (project?.image) return [{ path: project.image, isHero: true }];
        if (project?.immagine1) return [{ path: project.immagine1, isHero: true }];
        return [];
    };

    const galleryImages = getGalleryImages();

    const descriptionParts = useMemo(() => {
        const text = project?.descrizione || "";
        const words = text.trim().split(/\s+/);
        if (!text) return { short: "", long: "", needsToggle: false };
        if (words.length <= MAX_WORDS)
            return { short: text, long: text, needsToggle: false };
        return {
            short: words.slice(0, MAX_WORDS).join(" ") + "…",
            long: text,
            needsToggle: true
        };
    }, [project?.descrizione]);

    // ===== Normalize categorie from project =====
    const normalizeCategoriesFromProject = (p) => {
        if (!p) return [];
        if (Array.isArray(p.categorie) && p.categorie.length) {
            return p.categorie.map((id) => Number(id)).filter((id) => !isNaN(id));
        }
        if (Array.isArray(p.categorie_ids) && p.categorie_ids.length) {
            return p.categorie_ids.map((id) => Number(id)).filter((id) => !isNaN(id));
        }
        if (Array.isArray(p.categorie_nomi) && p.categorie_nomi.length) {
            return p.categorie_nomi
                .map((nome) => CATEGORY_MAP[nome] || null)
                .filter((id) => id !== null);
        }
        if (p.category_id) {
            const id = Number(p.category_id);
            return !isNaN(id) ? [id] : [];
        }
        return [];
    };

    // ===== Effects =====
    useEffect(() => {
        if (!project) return;

        const categorie = normalizeCategoriesFromProject(project);

        // immagini esistenti dal backend
        const imgs = Array.isArray(project.images)
            ? project.images.map((img, index) => ({
                path: img.path || img,
                isHero: !!img.isHero,
                isNew: false
            }))
            : [];

        // se nessuna hero marcata, segna la prima
        if (imgs.length && !imgs.some((i) => i.isHero)) {
            imgs[0].isHero = true;
        }

        const isImportant =
            project.isImportant ||
            project.is_important === 1 ||
            project.is_important === "1";

        setFormData({
            titolo: project.titolo || "",
            sottotitolo: project.sottotitolo || "",
            citta: project.citta || project.luogo || project.city || "",
            descrizione: project.descrizione || "",
            committente: project.committente || project.stazione_appaltante || "",
            incarico: project.incarico || "",
            inizio: project.inizio || project.data_inizio || "",
            fine: project.fine || project.data_fine || "",
            categorie,
            is_important: isImportant ? 1 : 0,
            isImportant: !!isImportant,
            images: imgs
        });
    }, [project]);

    useEffect(() => {
        if (!isOpen) {
            setCurrentView("details");
            setShowDeleteModal(false);
            setShowFullDesc(false);
        }
    }, [isOpen]);

    // ===== Image handlers =====
    const handleImagesChange = (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;

        let accepted = 0;
        files.forEach((f) => {
            if (!f.type.startsWith("image/")) {
                toastError(`${f.name} non è un'immagine valida`);
                return;
            }
            if (f.size > 5 * 1024 * 1024) {
                toastError(`${f.name} supera 5MB`);
                return;
            }
            accepted += 1;
            const r = new FileReader();
            r.onload = () =>
                setFormData((prev) => ({
                    ...prev,
                    images: [
                        ...prev.images,
                        {
                            file: f,
                            preview: r.result,
                            isNew: true,
                            isHero: prev.images.length === 0,
                            path: f.name
                        }
                    ]
                }));
            r.readAsDataURL(f);
        });

        if (accepted) toastSuccess(`${accepted} immagine/i pronta/e per il salvataggio`);
    };

    const removeImage = (i) => {
        setFormData((prev) => ({
            ...prev,
            images: prev.images.filter((_, idx) => idx !== i)
        }));
        toastInfo("Immagine rimossa dalla bozza");
    };

    const setHeroImage = (i) => {
        setFormData((prev) => ({
            ...prev,
            images: prev.images.map((img, idx) => ({ ...img, isHero: idx === i }))
        }));
        toastInfo("Impostata come immagine principale");
    };

    // ===== API: UPDATE =====
    const handleSubmit = async (e) => {
        e?.preventDefault?.();

        if (!formData.titolo?.trim()) return toastError("Titolo obbligatorio");
        if (!formData.citta?.trim()) return toastError("Città obbligatoria");
        if (!Array.isArray(formData.categorie) || formData.categorie.length === 0) {
            return toastError("Seleziona almeno una categoria.");
        }

        // normalizza booleano evidenza
        const important =
            typeof formData.isImportant === "boolean"
                ? formData.isImportant
                : Number(formData.is_important) === 1;

        // costruiamo existing_images per il backend
        const existing_images = formData.images.map((img) => ({
            path: img.path,
            isHero: !!img.isHero,
            isNew: !!img.isNew
        }));

        const fd = new FormData();
        fd.append("id", project.id);
        fd.append("titolo", formData.titolo);
        fd.append("sottotitolo", formData.sottotitolo || "");
        fd.append("descrizione", formData.descrizione || "");
        fd.append("citta", formData.citta);
        fd.append("committente", formData.committente || "");
        fd.append("incarico", formData.incarico || "");
        fd.append("inizio", formData.inizio || "");
        fd.append("fine", formData.fine || "");
        fd.append("is_important", important ? "1" : "0");

        // categorie: array → $_POST['categorie'][]
        formData.categorie.forEach((cid) => {
            fd.append("categorie[]", String(cid));
        });

        // nuove immagini
        let newIdx = 1;
        formData.images
            .filter((img) => img.isNew && img.file)
            .forEach((image) => {
                fd.append(`image_${newIdx}`, image.file);
                newIdx += 1;
            });

        // stato immagini (ordine + hero)
        fd.append("existing_images", JSON.stringify(existing_images));


        setLoading(true);
        const t = toastLoading("Aggiornamento progetto in corso…");

        try {
            const res = await fetch(api("/updateProject.php"), {
                method: "POST",
                body: fd
            });

            if (!res.ok) throw new Error(`HTTP ${res.status}`);

            const text = await res.text();
            let result;
            try {
                result = JSON.parse(text);
            } catch (err) {
                console.error("Risposta non JSON da updateProject.php:", text);
                throw new Error("Risposta non valida dal server");
            }

            if (!result.success) {
                throw new Error(result.error || "Aggiornamento fallito");
            }

            updateToast(
                t,
                result.message || "Progetto aggiornato con successo!",
                "success"
            );
            onUpdate && onUpdate();
            onClose?.();
        } catch (err) {
            console.error(err);
            updateToast(
                t,
                `Errore durante l'aggiornamento: ${err?.message || "sconosciuto"}`,
                "error"
            );
        } finally {
            setLoading(false);
        }
    };

    // ===== API: DELETE =====
    const handleDelete = async (projectId) => {
        setLoading(true);
        const t = toastLoading("Eliminazione progetto in corso…");
        try {
            const res = await fetch(api("/deleteProjects.php"), {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ id: projectId })
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);

            const result = await res.json();
            if (!result.success) throw new Error(result.message || "Eliminazione fallita");

            updateToast(t, "Progetto eliminato con successo!", "success");
            onUpdate && onUpdate();
            onClose?.();
        } catch (err) {
            console.error(err);
            updateToast(
                t,
                `Errore durante l'eliminazione: ${err?.message || "sconosciuto"}`,
                "error"
            );
        } finally {
            setLoading(false);
            setShowDeleteModal(false);
        }
    };

    // ===== UI state handlers =====
    const handleEdit = () => {
        setCurrentView("edit");
        toastInfo("Modalità modifica attivata");
    };

    const handleCancel = () => {
        setCurrentView("details");
        setShowFullDesc(false);
        toastInfo("Modifiche annullate");
    };

    const handleSave = () => {
        handleSubmit();
    };

    if (!isOpen || !project) return null;

    // ========== DETAILS DIALOG ==========
    const DetailsDialog = (
        <Dialog
            open={isOpen && currentView === "details"}
            onClose={onClose}
            fullScreen={isMobile}
            maxWidth="lg"
            fullWidth
            scroll="paper"
            PaperProps={{
                elevation: 12,
                sx: {
                    borderRadius: isMobile ? 0 : 4,
                    boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
                    overflow: "hidden",
                    bgcolor: BG_DETAILS,
                    maxHeight: "95vh"
                }
            }}
        >
            <DialogTitle
                component="div"
                sx={{
                    m: 0,
                    p: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    borderBottom: "none",
                    bgcolor: BG_DETAILS
                }}
            >
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{
                        color: "text.primary",
                        "&:hover": { backgroundColor: "action.hover" }
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent
                sx={{
                    p: 0,
                    maxHeight: { xs: "calc(90vh - 56px)", sm: "calc(90vh - 96px)" },
                    overflow: "auto",
                    position: "relative"
                }}
                dividers={false}
            >
                {loading && (
                    <Backdrop
                        open
                        sx={{
                            color: "#fff",
                            zIndex: (t) => t.zIndex.drawer + 1,
                            position: "absolute",
                            backgroundColor: "rgba(0,0,0,0.55)"
                        }}
                    >
                        <CircularProgress color="inherit" />
                    </Backdrop>
                )}
                <Box sx={{ bgcolor: BG_DETAILS }}>
                    <Box
                        sx={{
                            mx: "auto",
                            maxWidth: 1100,
                            px: { xs: 2, sm: 3, md: 4 },
                            py: { xs: 2, sm: 3 }
                        }}
                    >
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: {
                                    xs: "1fr",
                                    md: "minmax(280px, 1fr) 1fr"
                                },
                                gap: { xs: 2, sm: 3 },
                                alignItems: "start"
                            }}
                        >
                            {/* Galleria immagini */}
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 1.5
                                }}
                            >
                                {(galleryImages.length
                                        ? galleryImages
                                        : [{ path: null }]
                                ).map((img, i) => (
                                    <Card
                                        key={i}
                                        sx={{
                                            borderRadius: 2,
                                            overflow: "hidden",
                                            bgcolor: img.path
                                                ? "transparent"
                                                : "grey.50",
                                            border: img.path
                                                ? "none"
                                                : "2px dashed",
                                            borderColor: "grey.300"
                                        }}
                                    >
                                        {img.path ? (
                                            <CardMedia
                                                component="img"
                                                image={
                                                    img.path.startsWith("http") ||
                                                    img.path.startsWith("/")
                                                        ? img.path
                                                        : api(`/Progetti/${project.id}/${img.path}`)
                                                }
                                                alt={`img-${i}`}
                                                sx={{
                                                    width: "100%",
                                                    aspectRatio: "4 / 3",
                                                    objectFit: "cover",
                                                    display: "block"
                                                }}
                                            />
                                        ) : (
                                            <Box
                                                sx={{
                                                    p: 2,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    aspectRatio: "4 / 3"
                                                }}
                                            >
                                                <ImageIcon
                                                    sx={{
                                                        fontSize: 40,
                                                        color: "grey.400"
                                                    }}
                                                />
                                            </Box>
                                        )}
                                    </Card>
                                ))}
                            </Box>

                            {/* Testi */}
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 1.5,
                                    textAlign: "right"
                                }}
                            >
                                <Box sx={{ mb: 0.5 }}>
                                    <Typography
                                        variant="h5"
                                        fontWeight={800}
                                        sx={{
                                            lineHeight: 1.15,
                                            wordBreak: "break-word",
                                            fontFamily: "Poppins, sans-serif"
                                        }}
                                    >
                                        {project.titolo}
                                    </Typography>
                                    {project.sottotitolo && (
                                        <Typography
                                            fontWeight={700}
                                            sx={{
                                                mt: 0.3,
                                                fontSize: { xs: "1rem", sm: "1.15rem", md: "1.3rem" },
                                                lineHeight: 1.15,
                                                wordBreak: "break-word",
                                                fontFamily: "Poppins, sans-serif"
                                            }}
                                        >
                                            {project.sottotitolo}
                                        </Typography>
                                    )}
                                </Box>

                                <Box
                                    sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 0.75
                                    }}
                                >
                                    <Box>
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                display: "block",
                                                fontWeight: 700,
                                                color: themeColors.primary,
                                                fontSize: "0.75rem",
                                                fontFamily:
                                                    "Poppins, sans-serif"
                                            }}
                                        >
                                            Località
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontFamily:
                                                    "Poppins, sans-serif"
                                            }}
                                        >
                                            {project.citta ||
                                                project.luogo ||
                                                project.city ||
                                                "—"}
                                        </Typography>
                                    </Box>

                                    <Box>
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                display: "block",
                                                fontWeight: 700,
                                                color: themeColors.primary,
                                                fontSize: "0.75rem",
                                                fontFamily:
                                                    "Poppins, sans-serif"
                                            }}
                                        >
                                            Anno
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontFamily:
                                                    "Poppins, sans-serif"
                                            }}
                                        >
                                            {project.data_inizio ||
                                                project.inizio ||
                                                "—"}
                                            {(project.data_fine ||
                                                    project.fine) &&
                                                " – "}
                                            {project.data_fine ||
                                                project.fine ||
                                                ""}
                                        </Typography>
                                    </Box>

                                    {project.committente && (
                                        <Box>
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    display: "block",
                                                    fontWeight: 700,
                                                    color: themeColors.primary,
                                                    fontSize: "0.75rem",
                                                    fontFamily:
                                                        "Poppins, sans-serif"
                                                }}
                                            >
                                                Committente
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontFamily:
                                                        "Poppins, sans-serif"
                                                }}
                                            >
                                                {project.committente}
                                            </Typography>
                                        </Box>
                                    )}

                                    {project.incarico && (
                                        <Box>
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    display: "block",
                                                    fontWeight: 700,
                                                    color: themeColors.primary,
                                                    fontSize: "0.75rem",
                                                    fontFamily:
                                                        "Poppins, sans-serif"
                                                }}
                                            >
                                                Incarico
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontFamily:
                                                        "Poppins, sans-serif"
                                                }}
                                            >
                                                {project.incarico}
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>

                                {project.descrizione && (
                                    <Box sx={{ mt: 0.5 }}>
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                display: "block",
                                                fontWeight: 700,
                                                color: themeColors.primary,
                                                fontSize: "0.75rem",
                                                fontFamily:
                                                    "Poppins, sans-serif",
                                                textAlign: "right",
                                                mb: 1
                                            }}
                                        >
                                            Descrizione
                                        </Typography>

                                        <Typography
                                            variant="body2"
                                            sx={{
                                                whiteSpace: "pre-line",
                                                wordBreak: "break-word",
                                                lineHeight: 1.5,
                                                textAlign: "right",
                                                fontFamily:
                                                    "Poppins, sans-serif"
                                            }}
                                        >
                                            {showFullDesc
                                                ? descriptionParts.long
                                                : descriptionParts.short}
                                        </Typography>
                                        {descriptionParts.needsToggle && (
                                            <Box
                                                sx={{
                                                    mt: 1,
                                                    textAlign: "right"
                                                }}
                                            >
                                                <Button
                                                    onClick={() =>
                                                        setShowFullDesc(
                                                            (v) => !v
                                                        )
                                                    }
                                                    size="small"
                                                    sx={{
                                                        borderRadius: 1.5,
                                                        px: 2,
                                                        py: 0.5,
                                                        fontWeight: 600,
                                                        fontSize: "0.8rem",
                                                        color: themeColors.primary,
                                                        "&:hover": {
                                                            backgroundColor:
                                                                alpha(
                                                                    themeColors.primary,
                                                                    0.08
                                                                )
                                                        },
                                                        fontFamily:
                                                            "Poppins, sans-serif"
                                                    }}
                                                >
                                                    {showFullDesc
                                                        ? "Mostra di meno"
                                                        : "Mostra di più"}
                                                </Button>
                                            </Box>
                                        )}
                                    </Box>
                                )}
                            </Box>
                        </Box>

                        {/* Footer */}
                        <Box
                            sx={{
                                mt: { xs: 2, sm: 2.5 },
                                pt: { xs: 1.5, sm: 2 },
                                borderTop: "1px solid",
                                borderColor: "divider",
                                display: "flex",
                                flexDirection: {
                                    xs: "column",
                                    sm: "row"
                                },
                                gap: 1,
                                alignItems: {
                                    xs: "stretch",
                                    sm: "center"
                                },
                                justifyContent: "space-between"
                            }}
                        >
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                    order: { xs: 2, sm: 1 },
                                    fontSize: "0.7rem",
                                    fontFamily:
                                        "Poppins, sans-serif"
                                }}
                            >
                                © {new Date().getFullYear()} Esi Pro |
                                Architettura e ingegneria. All
                                rights reserved
                            </Typography>
                            <Stack
                                direction="row"
                                spacing={1}
                                sx={{
                                    order: { xs: 1, sm: 2 },
                                    justifyContent: "flex-end"
                                }}
                            >
                                <Button
                                    startIcon={<EditIcon />}
                                    onClick={handleEdit}
                                    disabled={loading}
                                    size="small"
                                    sx={{
                                        backgroundColor:
                                        themeColors.success,
                                        color: "#fff",
                                        borderRadius: 1.5,
                                        px: 2,
                                        py: 0.75,
                                        fontSize: "0.8rem",
                                        "&:hover": {
                                            backgroundColor: "#1b5e20"
                                        },
                                        fontFamily:
                                            "Poppins, sans-serif"
                                    }}
                                >
                                    Modifica
                                </Button>
                                <Button
                                    startIcon={<DeleteIcon />}
                                    onClick={() =>
                                        setShowDeleteModal(true)
                                    }
                                    disabled={loading}
                                    size="small"
                                    sx={{
                                        backgroundColor:
                                        themeColors.error,
                                        color: "#fff",
                                        borderRadius: 1.5,
                                        px: 2,
                                        py: 0.75,
                                        fontSize: "0.8rem",
                                        "&:hover": {
                                            backgroundColor: "#c62828"
                                        },
                                        fontFamily:
                                            "Poppins, sans-serif"
                                    }}
                                >
                                    Elimina
                                </Button>
                            </Stack>
                        </Box>
                    </Box>
                </Box>
            </DialogContent>

            <Backdrop
                sx={{
                    color: "#fff",
                    zIndex: (t) => t.zIndex.drawer + 1,
                    backgroundColor: alpha("#000", 0.8)
                }}
                open={loading}
            >
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 2,
                        p: 3,
                        bgcolor: "#fff",
                        borderRadius: 2,
                        boxShadow: "0 10px 40px rgba(0,0,0,.3)"
                    }}
                >
                    <CircularProgress
                        size={40}
                        sx={{ color: themeColors.primary }}
                    />
                    <Typography
                        variant="h6"
                        sx={{
                            color: themeColors.primary,
                            fontWeight: 600,
                            fontSize: "1rem",
                            fontFamily:
                                "Poppins, sans-serif"
                        }}
                    >
                        Caricamento…
                    </Typography>
                </Box>
            </Backdrop>
        </Dialog>
    );

    // ========== EDIT DIALOG ==========
    const EditDialog = (
        <Dialog
            open={isOpen && currentView === "edit"}
            onClose={onClose}
            fullScreen={isMobile}
            maxWidth="md"
            fullWidth
            scroll="paper"
            PaperProps={{
                elevation: 12,
                sx: {
                    borderRadius: isMobile ? 0 : 4,
                    overflow: "hidden",
                    bgcolor: "#fff"
                }
            }}
        >
            <DialogTitle
                component="div"
                sx={{
                    m: 0,
                    p: 3,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    background: `linear-gradient(90deg, ${themeColors.primary} 0%, ${themeColors.secondary} 100%)`,
                    color: "#fff"
                }}
            >
                <Typography
                    variant="h6"
                    fontWeight={700}
                    sx={{ fontFamily: "Poppins, sans-serif" }}
                >
                    Modifica Progetto
                </Typography>
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{
                        color: "#fff",
                        "&:hover": {
                            backgroundColor: alpha("#fff", 0.12)
                        }
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent
                sx={{
                    p: 0,
                    maxHeight: {
                        xs: "calc(100vh - 56px)",
                        sm: "calc(100vh - 96px)"
                    }
                }}
            >
                <Box
                    sx={{
                        p: { xs: 2, sm: 3, md: 4 },
                        bgcolor: "#fff"
                    }}
                >
                    <Box sx={{ mx: "auto", maxWidth: 900 }}>
                        <ProjectForm
                            formData={formData}
                            setFormData={setFormData}
                            handleImagesChange={handleImagesChange}
                            removeImage={removeImage}
                            setHeroImage={setHeroImage}
                            handleSubmit={handleSubmit}
                            loading={loading}
                            onCancel={handleCancel}
                            projectId={project.id}
                            isEdit
                        />
                    </Box>
                </Box>
            </DialogContent>

            {isMobile && (
                <Box
                    sx={{
                        position: "fixed",
                        bottom: 16,
                        right: 16,
                        zIndex: 1000
                    }}
                >
                    <Stack direction="column" spacing={1}>
                        <Fab
                            color="primary"
                            onClick={handleSave}
                            disabled={loading}
                            sx={{
                                bgcolor: themeColors.success,
                                "&:hover": { bgcolor: "#1b5e20" }
                            }}
                        >
                            <SaveIcon />
                        </Fab>
                        <Fab
                            color="secondary"
                            onClick={handleCancel}
                            disabled={loading}
                            sx={{
                                bgcolor: themeColors.error,
                                "&:hover": { bgcolor: "#c62828" }
                            }}
                        >
                            <CancelIcon />
                        </Fab>
                    </Stack>
                </Box>
            )}
        </Dialog>
    );

    return (
        <>
            {DetailsDialog}
            {EditDialog}

            {showDeleteModal && (
                <ConfirmDeleteModal
                    isOpen={showDeleteModal}
                    project={project}
                    onConfirm={() => handleDelete(project.id)}
                    onCancel={() => setShowDeleteModal(false)}
                />
            )}
        </>
    );
};

export default ProtectedProjectModal;
